/**
 * Authentication API repository
 */

import { API_ENDPOINTS } from '../config/apiEndpoints';

const AUTH_API_URL = API_ENDPOINTS.AUTH;

export interface LoginRequest {
    username: string;
    password: string;
}

export interface AuthTokens {
    access: string;
    refresh: string;
    user: {
        uuid: string;
        username: string;
        email: string;
        is_staff: boolean;
        is_active: boolean;
        date_joined: string;
    };
}

export interface SignupRequest {
    username: string;
    email: string;
    password1: string;
    password2: string;
}

export interface SignupResponse {
    access?: string;
    refresh?: string;
    user?: {
        uuid: string;
        username: string;
        email: string;
        is_staff?: boolean;
        is_active?: boolean;
        date_joined?: string;
    };
}

export interface RefreshTokenResponse {
    access: string;
    refresh?: string;
}

export interface AuthError {
    error: string;
}

export const loginWithLambda = async (credentials: LoginRequest): Promise<AuthTokens | AuthError> => {
    try {
        const response = await fetch(`${AUTH_API_URL}/auth`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                request_type: 'login',
                username: credentials.username,
                password: credentials.password,
            }),
        });

        const data = await response.json();

        if (!response.ok) {
            return { error: data.error || `HTTP ${response.status}` };
        }

        return data as AuthTokens;
    } catch (error) {
        return { error: error instanceof Error ? error.message : 'Failed to login' };
    }
};

export const signupWithLambda = async (signupData: SignupRequest): Promise<SignupResponse | AuthError> => {
    try {
        const response = await fetch(`${AUTH_API_URL}/auth`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                request_type: 'signup',
                ...signupData,
            }),
        });

        const data = await response.json();

        if (!response.ok) {
            return { error: data.error || `HTTP ${response.status}` };
        }

        return data as SignupResponse;
    } catch (error) {
        return { error: error instanceof Error ? error.message : 'Failed to signup' };
    }
};

export const refreshTokenWithLambda = async (refreshToken: string): Promise<RefreshTokenResponse | AuthError> => {
    try {
        const response = await fetch(`${AUTH_API_URL}/auth`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                request_type: 'refresh_token',
                refresh: refreshToken,
            }),
        });

        const data = await response.json();

        if (!response.ok) {
            return { error: data.error || `HTTP ${response.status}` };
        }

        return data as RefreshTokenResponse;
    } catch (error) {
        return { error: error instanceof Error ? error.message : 'Failed to refresh token' };
    }
};

export const logoutWithLambda = async (): Promise<void> => {
    // Client-side logout - just clear tokens
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
};

export interface PasswordResetRequest {
    username: string;
}

export interface PasswordResetResponse {
    status: string;
    message: string;
}

export interface ResetPasswordRequest {
    reset_token: string;
    new_password: string;
}

export interface ResetPasswordResponse {
    status: string;
    message: string;
}

export const requestPasswordReset = async (
    payload: PasswordResetRequest
): Promise<PasswordResetResponse | AuthError> => {
    try {
        const response = await fetch(`${AUTH_API_URL}/auth`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ request_type: 'request_password_reset', ...payload }),
        });

        const data = await response.json();

        if (!response.ok) {
            return { error: data.error || `HTTP ${response.status}` };
        }

        return data as PasswordResetResponse;
    } catch (error) {
        return { error: error instanceof Error ? error.message : 'Failed to request password reset' };
    }
};

export interface ChangePasswordRequest {
    current_password: string;
    new_password: string;
    confirm_password: string;
}

export interface ChangePasswordResponse {
    status: string;
    message: string;
}

export const changePassword = async (
    payload: ChangePasswordRequest
): Promise<ChangePasswordResponse | AuthError> => {
    try {
        const accessToken = localStorage.getItem('access_token');
        if (!accessToken) {
            return { error: 'Not authenticated' };
        }

        const response = await fetch(`${AUTH_API_URL}/auth`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${accessToken}`,
            },
            body: JSON.stringify({ request_type: 'change_password', ...payload }),
        });

        const data = await response.json();

        if (!response.ok) {
            return { error: data.error || `HTTP ${response.status}` };
        }

        return data as ChangePasswordResponse;
    } catch (error) {
        return { error: error instanceof Error ? error.message : 'Failed to change password' };
    }
};

export const resetPassword = async (
    payload: ResetPasswordRequest
): Promise<ResetPasswordResponse | AuthError> => {
    try {
        const response = await fetch(`${AUTH_API_URL}/auth`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ request_type: 'reset_password', ...payload }),
        });

        const data = await response.json();

        if (!response.ok) {
            return { error: data.error || `HTTP ${response.status}` };
        }

        return data as ResetPasswordResponse;
    } catch (error) {
        return { error: error instanceof Error ? error.message : 'Failed to reset password' };
    }
};
